/* eslint-disable react/react-in-jsx-scope */
import ManageLogo from "@/components/layout/ManageLogo";
import { Button } from "@/components/ui/button";
import { ColorPicker } from "@/components/ui/color-picker";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ToggleTheme } from "@/components/ui/ToggleTheme";
import { defaultThemes } from "@/libs/themesList";
import { HslColor } from "@uiw/react-color";
import { ChevronDown, Globe, Paintbrush, RefreshCcw, X } from "lucide-react";
import { Dispatch, SetStateAction, useState } from "react";

const changeableThemeValuesConfig: Array<{
  label: string;
  themeKey: keyof ThemeVars;
}> = [
  { label: "Arrière-plan général", themeKey: "--background" },
  { label: "Texte sur fond général", themeKey: "--foreground" },
  {
    label: "Couleur principale",
    themeKey: "--primary",
  },
  { label: "Texte sur couleur principale", themeKey: "--primary-foreground" },
  { label: "Couleur secondaire", themeKey: "--secondary" },
  { label: "Texte sur couleur secondaire", themeKey: "--secondary-foreground" },
  { label: "Fond discret", themeKey: "--muted" },
  { label: "Texte discret", themeKey: "--muted-foreground" },
  { label: "Couleur d'accentuation", themeKey: "--accent" },
  {
    label: "Texte sur couleur d'accentuation",
    themeKey: "--accent-foreground",
  },
  { label: "Bordures", themeKey: "--border" },
  // Ajouts spécifiques
  { label: "Couleur Prévu", themeKey: "--color-blue" },
  { label: "Couleur Réalisé", themeKey: "--color-green" },
  {
    label: "Couleur Vérifié / justifié / certifié",
    themeKey: "--color-orange",
  },
  { label: " Carte Indicateur — Variante 1 ", themeKey: "--color-gray" },
  { label: "Carte Indicateur — Variante 2", themeKey: "--color-red" },
];

type ThemeColors = Record<string, string>;

type ThemeValues = {
  name: string;
  value: string;
  colors: ThemeColors;
};
export function Themes({
  customColors,
  setCustomColors,
  isOpen,
  setIsOpen,
  handleResetTheme,
  saveCustomThemeToDatabase,
  isEditing,
  setIsEditing,
  setVisible,
}: {
  customColors: ThemeValues;
  setCustomColors: Dispatch<SetStateAction<ThemeValues>>;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  handleResetTheme: () => void;
  saveCustomThemeToDatabase: (colors: ThemeValues) => void;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  setVisible: (visible: boolean) => void;
}) {
  const [showMore, setShowMore] = useState(false);
  let key = 0;
  return (
    <div className="flex items-center flex-wrap gap-5 justify-center py-4 border border-accent bg-accent/40 p-2 shadow backdrop-blur-sm scrollbar-thin max-lg:overflow-x-auto max-sm:w-full px-4 rounded-lg">
      <ManageLogo />
      <Popover
        open={isOpen}
        onOpenChange={(open) => {
          if (!open && isEditing) {
            saveCustomThemeToDatabase(customColors);
          }
          setIsOpen(open);
        }}
      >
        <PopoverTrigger asChild>
          <Button className="bg-muted-foreground hover:bg-[#a558d4] flex gap-2 items-center text-muted">
            <Paintbrush /> Personnaliser
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80">
          <div className="flex flex-col gap-3 mb-4">
            <div className="text-lg font-bold text-center">
              Personnaliser le thème
            </div>
            <div className="flex justify-center">
              <ToggleTheme
                saveCustomThemeToDatabase={saveCustomThemeToDatabase}
                setCustomColors={setCustomColors}
                setIsEditing={setIsEditing}
                customColors={customColors}
              />
            </div>
            <hr />
          </div>
          <ScrollArea className="h-80 rounded-md">
            <div className="flex flex-col gap-3 py-4">
              {changeableThemeValuesConfig.map(({ label, themeKey }) => {
                key = key + 1;
                if (key < 8) {
                  return (
                    <ThemeValue
                      label={label}
                      themeKey={themeKey}
                      key={themeKey}
                      currentColor={customColors.colors[themeKey]}
                      onColorChange={(newColor) => {
                        setCustomColors((prev) => ({
                          ...prev,
                          colors: {
                            ...prev.colors,
                            [themeKey]: newColor.replace(/,\s*/g, " "),
                          },
                        }));
                        setIsEditing(true);
                      }}
                    />
                  );
                }
                if (key > 8 && showMore) {
                  return (
                    <ThemeValue
                      label={label}
                      themeKey={themeKey}
                      key={themeKey}
                      currentColor={customColors.colors[themeKey]}
                      onColorChange={(newColor) => {
                        setCustomColors((prev) => ({
                          ...prev,
                          colors: {
                            ...prev.colors,
                            [themeKey]: newColor.replace(/,\s*/g, " "),
                          },
                        }));
                        setIsEditing(true);
                      }}
                    />
                  );
                }
              })}
              {!showMore && (
                <div className="flex justify-center">
                  <button
                    className=" flex gap-2 items-center text-foreground border py-1 px-2 rounded-md hover:bg-foreground hover:text-background transition-all"
                    onClick={() => setShowMore(true)}
                  >
                    <ChevronDown /> Voir plus
                  </button>
                </div>
              )}
            </div>
          </ScrollArea>
        </PopoverContent>
      </Popover>

      <Popover>
        <PopoverTrigger asChild>
          <Button className="bg-muted-foreground hover:bg-[#a558d4] flex gap-2 items-center text-muted">
            <Globe /> Explorer
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[900px]">
          <RadioGroup
            value={customColors.value}
            onValueChange={(value) => {
              const selectedTheme = defaultThemes.find(
                (t) => t.value === value
              );
              if (selectedTheme) {
                setCustomColors(selectedTheme);
                saveCustomThemeToDatabase(selectedTheme as ThemeValues);
              }
              setIsEditing(true);
            }}
            className="space-y-2 grid grid-cols-5"
          >
            {defaultThemes.map((themeOption) => (
              <div
                key={themeOption.value}
                className={`p-3 relative hover:bg-gray-300 hover:text-gray-800 transition-all rounded-lg ${
                  customColors.value === themeOption.value
                    ? "bg-gray-200 text-gray-800"
                    : ""
                }`}
              >
                <RadioGroupItem
                  value={themeOption.value}
                  id={themeOption.value}
                  className="absolute left-0 top-0 w-full h-full hidden"
                />
                <Label
                  htmlFor={themeOption.value}
                  className="flex flex-col items-center gap-3 text-center"
                >
                  <div className="flex gap-1">
                    {Object.entries(themeOption.colors)
                      .filter(([key]) =>
                        ["--primary", "--secondary", "--accent"].includes(key)
                      )
                      .map(([key, color], index) => (
                        <div
                          key={index}
                          style={{ backgroundColor: `hsl(${color})` }}
                          className={`w-6 h-12 border border-gray-100 rounded-md index-${key}`}
                        />
                      ))}
                  </div>
                  <span className="min-h-7">{themeOption.name}</span>
                </Label>
              </div>
            ))}
          </RadioGroup>
        </PopoverContent>
      </Popover>

      <Button
        variant={"outline"}
        className="flex gap-2 items-center"
        onClick={(e) => {
          e.preventDefault();
          handleResetTheme();
        }}
      >
        <RefreshCcw /> Réinitialiser
      </Button>
      <Button
        variant={"outline"}
        className=" bg-red-700 hover:bg-red-800 text-white "
        onClick={(e) => {
          e.preventDefault();
          setVisible(false);
        }}
      >
        <X className="size-5" />
      </Button>
    </div>
  );
}

type ThemeVars = {
  [key: string]: string;
};

type ThemeValueProps = {
  themeKey: keyof ThemeVars;
  label: string;
  currentColor?: string;
  onColorChange: (newColor: string) => void;
};

const ThemeValue = ({
  label,
  themeKey,
  currentColor,
  onColorChange,
}: ThemeValueProps) => {
  const initialColor: HslColor = currentColor
    ? (() => {
        const match = currentColor.match(/([\d.]+)\s+([\d.]+)%\s+([\d.]+)%/);
        if (match) {
          return {
            h: parseFloat(match[1]),
            s: parseFloat(match[2]),
            l: parseFloat(match[3]),
          };
        }
        return { h: 0, s: 0, l: 50 }; // Fallback color
      })()
    : { h: 210, s: 100, l: 50 };
  return (
    <div className="flex items-center gap-2">
      <ColorPicker color={initialColor} onColorChange={onColorChange} />
      <Label className="flex-shrink-0">{label}</Label>
    </div>
  );
};
